import { getActionTakersCount } from './action-takers';
import { getActionType, getCampaignId } from './actions';
import { numbersOnly, removeExtraWhitespace, removeStartEndNonBreakingSpaces } from './strings';

const ANIMATION_RENDER_DELAY_SECONDS = 0.25;

const COUNT_ELEMENT_HORIZONTAL_PADDING_PX = 8;

// Make sure duration matches .alz-widget-progress .enWidget__fill transition in _blocks.scss
const PROGRESS_BAR_ANIMATION_DURATION_SECONDS = 2;

const SETTINGS_LENGTH = 5;

/**
 * Set up a progress bar widget in a text block based on the settings provided.
 */
export const progressBarWidget = async () => {
  const progressBarTextBlock = document.querySelector('.alz-widget-progress') as HTMLElement | null;
  if (!progressBarTextBlock) return;

  // Get settings and then remove from text block
  const settings = progressBarTextBlock.querySelectorAll('li');
  if (!settings || settings.length < SETTINGS_LENGTH) return;

  let textAbove = removeStartEndNonBreakingSpaces(settings[0].innerHTML).trim() || '';
  let textBelow = removeStartEndNonBreakingSpaces(settings[1].innerHTML).trim() || '';
  const startCount = parseInt(numbersOnly(settings[2].textContent.trim()) || '0', 10);
  const targets =
    removeExtraWhitespace(settings[3].textContent)
      .split(' ')
      .map((text: string) => parseInt(numbersOnly(text), 10)) ||
    [].filter((target) => !isNaN(target) && target > 0);
  targets.sort((a, b) => a - b); // Ensure targets are in ascending order
  const thresholdPercent = parseInt(settings[4].textContent.trim() || '0', 10);

  progressBarTextBlock.innerHTML = '';

  // Get the number of action takers so far

  const actionType = getActionType();
  const campaignId = getCampaignId();
  const count = startCount + ((await getActionTakersCount({ actionType, campaignId })) || 0);

  // Calculate the target and progress

  const currentTarget =
    targets.find((target) => count < target * (thresholdPercent / 100)) ||
    targets[targets.length - 1];

  const percentComplete = Math.min(Math.round((count / currentTarget) * 100), 100);

  textAbove = replaceInsertCodesInText({
    count,
    target: currentTarget,
    percentComplete,
    text: textAbove,
  });

  textBelow = replaceInsertCodesInText({
    count,
    percentComplete,
    target: currentTarget,
    text: textBelow,
  });

  // Display the progress bar with the text

  progressBarTextBlock.innerHTML = `
    <div class="en__component en__component--widgetblock">
      <div class="enWidget enWidget--progressBar">
        ${textAbove ? `<div class="enWidget__copy enWidget__copy--above"><p>${textAbove}</p></div>` : ''}

        <div class="enWidget__display">
          <div class="enWidget__bar">
            <div class="enWidget__progress">
              <div class="enWidget__fill">
                <span class="enWidget__fill__count enWidget__fill__count--hidden">
                  ${count.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        ${textBelow ? `<div class="enWidget__copy enWidget__copy--below"><p>${textBelow}</p></div>` : ''}
      </div>
    </div>
  `;

  progressBarTextBlock.style.visibility = 'visible';

  // Animate the progress bar and number
  const fillElement = progressBarTextBlock.querySelector('.enWidget__fill') as HTMLElement | null;

  if (fillElement) {
    // Set the progress bar width after a short delay for HTML above to render - then CSS transition will animate it

    setTimeout(
      () => (fillElement.style.width = `${percentComplete}%`),
      ANIMATION_RENDER_DELAY_SECONDS * 1000,
    );

    // If progress bar is large enough, show the count after progress bar has animated

    const countElement = fillElement.querySelector('.enWidget__fill__count') as HTMLElement | null;

    setTimeout(
      () => {
        if (
          countElement &&
          countElement.getBoundingClientRect().width + COUNT_ELEMENT_HORIZONTAL_PADDING_PX <=
            fillElement.getBoundingClientRect().width
        )
          countElement.classList.remove('enWidget__fill__count--hidden');
      },
      (ANIMATION_RENDER_DELAY_SECONDS +
        PROGRESS_BAR_ANIMATION_DURATION_SECONDS +
        ANIMATION_RENDER_DELAY_SECONDS) *
        1000,
    );
  }
};

const replaceInsertCodesInText = (params: {
  count: number;
  percentComplete: number;
  target: number;
  text: string;
}) => {
  const { count, percentComplete, target, text } = params;

  return text
    .replace(/{widget_data~count}/g, count.toLocaleString())
    .replace(/{widget_data~percent}/g, `${percentComplete}%`)
    .replace(/{widget_data~remaining}/g, Math.max(target - count, 0).toLocaleString())
    .replace(/{widget_data~target}/g, target.toLocaleString())
    .trim();
};
